import { getRandomEstimateColor } from '@components/ArtBoard/Estimator/constants'
import {
  type EstimateCurrency,
  type EstimateUnits,
  Factory,
  type ID,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'

type ChangeEstimatesHiddenPayload = {
  hidden: boolean
}

type ChangeEstimatesUnitsPayload = {
  units: EstimateUnits
}

type ChangeEstimatesCurrencyPayload = {
  currency: EstimateCurrency
}

type ChangeEstimatesTaxEnabledPayload = {
  taxEnabled: boolean
}

type UpdateEstimatesTaxValue = {
  taxValue: number
}

type UpdateLineItemTitlePayload = {
  id: ID
  title: string
}

type UpdateLineItemIndexPayload = {
  id: ID
  index: number
}

type UpdateLineItemLinkedPayload = {
  id: ID
  linked: boolean
}

export const changeEstimatesHidden =
  ({ hidden }: ChangeEstimatesHiddenPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.changeSettingsHidden({
          hidden,
        })
      )
    )
  }

export const changeEstimatesUnits =
  ({ units }: ChangeEstimatesUnitsPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.changeSettingsUnits({
          units,
        })
      )
    )
  }

export const changeEstimatesCurrency =
  ({ currency }: ChangeEstimatesCurrencyPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.changeSettingsCurrency({
          currency,
        })
      )
    )
  }

export const changeEstimatesTaxEnabled =
  ({ taxEnabled }: ChangeEstimatesTaxEnabledPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.changeSettingsTaxEnabled({
          taxEnabled,
        })
      )
    )
  }

export const updateEstimatesTaxValue =
  ({ taxValue }: UpdateEstimatesTaxValue): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.updateSettingsTaxValue({
          taxValue,
        })
      )
    )
  }

export const addEstimateLineItem = (): AppThunk => (dispatch) => {
  const id = getId()

  const lineItem = Factory.estimateLineItem({
    id,
    rate: 45,
    color: getRandomEstimateColor(),
  })

  dispatch(commitProjectRequest(ProjectActions.estimates.addLineItem(lineItem)))

  return id
}

export const updateLineItemIndex =
  ({ id, index }: UpdateLineItemIndexPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.updateLineItemIndex({ id, index })
      )
    )
  }

export const changeLineItemLinked =
  ({ id, linked }: UpdateLineItemLinkedPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.changeLineItemLinked({ id, linked })
      )
    )
  }

export const deleteEstimateLineItem =
  (id: ID): AppThunk =>
  (dispatch) => {
    // @todo implement logic to find hours and ask before delete
    // const handleRemoveEstimate = (id: string) => {
    //   const findHours = items.find((item) => item.id === id)
    //
    //   if (findHours && findHours.hours === 0) {
    //     dispatch({
    //       type: ActionTypes.COMMIT_PROJECT_REQUEST,
    //       payload: {
    //         type: ActionTypes.DELETE_ESTIMATE,
    //         payload: { lineItemId: id },
    //       },
    //       history: false,
    //     })
    //
    //     return
    //   }
    //
    //   dispatch(
    //     openSystemDialog(DialogTypes.DELETE_ESTIMATE, {
    //       title: 'Unlink estimate',
    //       text: 'Are you sure you want to unlink this data row from sitemap? All inputted hours will be reset to zero.',
    //       btnText: 'Unlink',
    //       onSuccess: () => {
    //         dispatch({
    //           type: ActionTypes.COMMIT_PROJECT_REQUEST,
    //           payload: {
    //             type: ActionTypes.DELETE_ESTIMATE,
    //             payload: { lineItemId: id },
    //           },
    //           history: false,
    //         })
    //       },
    //     })
    //   )
    // }

    dispatch(
      commitProjectRequest(ProjectActions.estimates.deleteLineItem({ id }))
    )
  }

export const updateEstimateLineItemTitle =
  (payload: UpdateLineItemTitlePayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.updateLineItemTitle(payload)
      )
    )
  }

type UpdateLineItemRatePayload = {
  id: ID
  rate: number
}

export const updateEstimateLineItemRate =
  (payload: UpdateLineItemRatePayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(ProjectActions.estimates.updateLineItemRate(payload))
    )
  }

type UpdateLineItemValuePayload = {
  id: ID
  value: number
}

export const updateEstimateLineItemValue =
  (payload: UpdateLineItemValuePayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.estimates.updateLineItemValue(payload)
      )
    )
  }

type UpdateEstimateNodeItemPayload = {
  nodeId: ID
  lineItemId: ID
  value: number
}

export const updateEstimateNodeItem =
  ({ nodeId, lineItemId, value }: UpdateEstimateNodeItemPayload): AppThunk =>
  (dispatch, getState) => {
    const estimateNodeItem =
      migrationProjectSelector.getProjectNodeEstimatesByNodeIdAndLineItemId(
        nodeId,
        lineItemId
      )(getState())

    if (!value && estimateNodeItem) {
      dispatch(
        commitProjectRequest(
          ProjectActions.estimates.delete({ ids: [estimateNodeItem.id] })
        )
      )

      return
    }

    if (value && !estimateNodeItem) {
      const estimateItem = Factory.estimate({
        id: getId(),
        lineItemId,
        value,
      })

      dispatch(
        commitProjectRequest(
          ProjectActions.estimates.add({
            items: [estimateItem],
            index: 0,
            parents: [nodeId],
          })
        )
      )

      return
    }

    if (value && estimateNodeItem) {
      dispatch(
        commitProjectRequest(
          ProjectActions.estimates.updateEstimateValue({
            id: estimateNodeItem.id,
            value,
          })
        )
      )
    }
  }
